import { useState } from 'react';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Champ } from './Elements';
import type { CompteBancaire } from '../types';

/**
 * LES COORDONNÉES BANCAIRES d'une école (2026-10-17) : la table existait
 * depuis le socle et le bordereau de remise l'imprimait, sans aucun écran
 * — ses coordonnées étaient celles du seed. Le bloc règle le libellé, le
 * titulaire, l'IBAN et le BIC ; le serveur vérifie la clé de l'IBAN et en
 * déduit banque, guichet et compte pour le bordereau.
 *
 * Même grammaire que les variables de paie : le bloc écrit immédiatement,
 * chaque geste est un acte à part. Un compte qui a servi à une remise se
 * désactive, il ne se supprime pas.
 */

/** Par groupes de quatre, pour l'œil ; la valeur stockée est compacte. */
const formaterIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

interface Saisie {
  libelle: string;
  titulaire: string;
  iban: string;
  bic: string;
}

const VIDE: Saisie = { libelle: '', titulaire: '', iban: '', bic: '' };

export function ComptesBancaires({ entiteId, peutModifier }: { entiteId: string; peutModifier: boolean }) {
  const comptes = useRessource(
    () => api.get<CompteBancaire[]>(`/api/admin/entites/${entiteId}/comptes-bancaires`),
    [entiteId],
  );
  const [saisie, setSaisie] = useState<Saisie>(VIDE);
  const [edition, setEdition] = useState<(Saisie & { id: string }) | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const agir = async (travail: () => Promise<unknown>) => {
    setErreur(null);
    setEnCours(true);
    try {
      await travail();
      comptes.recharger();
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi ? (cause.details?.[0]?.message ?? cause.message) : 'Enregistrement impossible.',
      );
    } finally {
      setEnCours(false);
    }
  };

  const corps = (s: Saisie) => ({
    libelle: s.libelle.trim(),
    titulaire: s.titulaire.trim(),
    iban: s.iban.trim() || null,
    bic: s.bic.trim() || null,
  });

  const ajouter = () => {
    if (!saisie.libelle.trim() || !saisie.titulaire.trim()) {
      setErreur('Indiquez un libellé et un titulaire.');
      return;
    }
    void agir(async () => {
      await api.post(`/api/admin/entites/${entiteId}/comptes-bancaires`, corps(saisie));
      setSaisie(VIDE);
    });
  };

  const enregistrerEdition = () => {
    if (!edition) return;
    void agir(async () => {
      await api.patch(`/api/admin/entites/${entiteId}/comptes-bancaires/${edition.id}`, corps(edition));
      setEdition(null);
    });
  };

  const basculer = (c: CompteBancaire) =>
    void agir(() => api.patch(`/api/admin/entites/${entiteId}/comptes-bancaires/${c.id}`, { actif: !c.actif }));

  const supprimer = (c: CompteBancaire) => {
    if (!window.confirm(`Supprimer le compte « ${c.libelle} » ?`)) return;
    void agir(() => api.delete(`/api/admin/entites/${entiteId}/comptes-bancaires/${c.id}`));
  };

  const champs = (s: Saisie, poser: (suite: Partial<Saisie>) => void, compact = false) => {
    const taille = compact ? 'py-1' : 'py-1.5';
    return (
      <>
        <input
          className={`champ ${taille}`}
          placeholder="Compte courant"
          aria-label="Libellé"
          value={s.libelle}
          onChange={(e) => poser({ libelle: e.target.value })}
        />
        <input
          className={`champ ${taille}`}
          placeholder="ASSOCIATION ARTISTIC"
          aria-label="Titulaire"
          value={s.titulaire}
          onChange={(e) => poser({ titulaire: e.target.value })}
        />
        <input
          className={`champ tabulaire ${taille}`}
          placeholder="FR76 …"
          aria-label="IBAN"
          value={s.iban}
          onChange={(e) => poser({ iban: e.target.value })}
        />
        <input
          className={`champ tabulaire ${taille}`}
          placeholder="SOGEFRPP"
          aria-label="BIC"
          value={s.bic}
          onChange={(e) => poser({ bic: e.target.value })}
        />
      </>
    );
  };

  const lignes = comptes.donnees ?? [];

  return (
    <div className="space-y-3">
      {peutModifier && (
        <div className="grid grid-cols-[10rem_12rem_1fr_8rem_auto] items-end gap-2">
          <Champ libelle="Libellé">{null}</Champ>
          <Champ libelle="Titulaire">{null}</Champ>
          <Champ libelle="IBAN">{null}</Champ>
          <Champ libelle="BIC">{null}</Champ>
          <span />
          {champs(saisie, (suite) => setSaisie({ ...saisie, ...suite }))}
          <button type="button" className="bouton-secondaire py-1.5" disabled={enCours} onClick={ajouter}>
            Ajouter
          </button>
        </div>
      )}

      {comptes.erreur && <p className="text-sm text-red-700">{comptes.erreur}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
            <th className="py-1 pr-2 font-medium">Libellé</th>
            <th className="py-1 pr-2 font-medium">Titulaire</th>
            <th className="py-1 pr-2 font-medium">IBAN</th>
            <th className="py-1 pr-2 font-medium">BIC</th>
            <th className="py-1 pr-2 font-medium">État</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100">
          {lignes.length === 0 && !comptes.chargement && (
            <tr>
              <td colSpan={6} className="py-2 text-encre-400">
                Aucun compte : le bordereau de remise sortira sans coordonnées.
              </td>
            </tr>
          )}
          {lignes.map((c) =>
            edition?.id === c.id ? (
              <tr key={c.id} className="bg-encre-50">
                <td colSpan={4} className="py-1.5 pr-2">
                  <div className="grid grid-cols-[10rem_12rem_1fr_8rem] gap-2">
                    {champs(edition, (suite) => setEdition({ ...edition, ...suite }), true)}
                  </div>
                </td>
                <td className="py-1.5 pr-2" />
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="text-xs underline" disabled={enCours} onClick={enregistrerEdition}>
                    Enregistrer
                  </button>
                  <button type="button" className="ml-2 text-xs text-encre-500 underline" onClick={() => setEdition(null)}>
                    Annuler
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={c.id} className={c.actif ? '' : 'text-encre-400'}>
                <td className="py-1.5 pr-2 font-medium">{c.libelle}</td>
                <td className="py-1.5 pr-2">{c.titulaire}</td>
                <td className="tabulaire whitespace-nowrap py-1.5 pr-2">
                  {c.iban ? formaterIban(c.iban) : <span className="text-encre-300">—</span>}
                </td>
                <td className="tabulaire py-1.5 pr-2">{c.bic ?? <span className="text-encre-300">—</span>}</td>
                <td className="py-1.5 pr-2">
                  <Badge ton={c.actif ? 'succes' : 'neutre'}>{c.actif ? 'actif' : 'inactif'}</Badge>
                  {c.nb_remises > 0 && (
                    <span className="ml-2 text-xs text-encre-500">
                      {c.nb_remises} remise{c.nb_remises > 1 ? 's' : ''}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {peutModifier && (
                    <>
                      <button
                        type="button"
                        className="text-xs underline"
                        onClick={() =>
                          setEdition({
                            id: c.id,
                            libelle: c.libelle,
                            titulaire: c.titulaire,
                            iban: c.iban ? formaterIban(c.iban) : '',
                            bic: c.bic ?? '',
                          })
                        }
                      >
                        Modifier
                      </button>
                      <button type="button" className="ml-2 text-xs underline" disabled={enCours} onClick={() => basculer(c)}>
                        {c.actif ? 'Désactiver' : 'Activer'}
                      </button>
                      {c.nb_remises === 0 && (
                        <button type="button" className="ml-2 text-xs text-red-600 underline" onClick={() => supprimer(c)}>
                          Supprimer
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      {erreur && <p className="text-sm text-red-700">{erreur}</p>}

      <p className="text-xs text-encre-500">
        Le premier compte actif est celui des remises de chèques : son titulaire, sa banque, son guichet et
        son numéro, déduits de l’IBAN, s’impriment sur le bordereau. Un compte qui a servi à une remise se
        désactive, il ne se supprime pas.
      </p>
    </div>
  );
}
